import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../index.css';
import loginImg from '../images/login.png';
import PasswordResetModal from './PasswordResetModal';
import jrslogo from '../images/jrslogo.png';
const API_URL = '/api';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch company logo on component mount
  useEffect(() => {
  const fetchCompanyLogo = async () => {
    try {
      setLogoLoading(true);
      const response = await fetch(`${API_URL}/vendoradmin/logo`);
      const data = await response.json();

      if (data.success && data.logo) {
        setCompanyLogo(data.logo); // already a base64 string with proper prefix
      } else {
        setCompanyLogo(null); // no logo found
      }
    } catch (error) {
      console.error('Error fetching Vendoradmin logo:', error);
      setCompanyLogo(null);
    } finally {
      setLogoLoading(false);
    }
  };

  fetchCompanyLogo();
}, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Login failed');
      } else {
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }
        try {
          await fetch('/api/store-user', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user: data.user }),
          });
        } catch (storeError) {
          console.error('Failed to store user in backend:', storeError);
        }
        onLogin && onLogin(data.user);
        navigate('/dashboard', { state: { user: data.user } });
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#AAD7D9] to-[#FBF9F1] relative overflow-hidden">
    
    {/* Background Blobs */}
    <div className="absolute -top-20 -left-20 w-60 h-60 md:w-72 md:h-72 bg-[#52ab98] rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
    <div className="absolute -bottom-20 -right-20 w-60 h-60 md:w-72 md:h-72 bg-[#2b3577] rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
    
    <div className="flex w-full max-w-6xl backdrop-blur-xl bg-white/70 rounded-3xl shadow-2xl border border-white/30 overflow-hidden mx-4 sm:mx-6 relative">
      
      {/* Company Logo */}
      <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
        {logoLoading ? (
  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-full animate-pulse"></div>
) : companyLogo ? (
  <img 
    src={companyLogo} 
    alt="Vendoradmin Logo" 
    className="w-12 h-12 sm:w-16 sm:h-16 object-contain rounded-full bg-white p-1 shadow-md"
  />
) : null}

      </div>

      {/* Left Illustration */}
      <div className="hidden md:flex flex-col justify-center items-center w-1/2 bg-gradient-to-br from-[#2b6777] to-[#1e4252]" style={{ borderTopRightRadius: '50rem' }}>
        <img src={loginImg} alt="3D Character" className="w-full h-full object-contain" />
      </div>
      
      {/* Right: Login Form */}
      <div className="w-full md:w-1/2 p-6 sm:p-10 flex flex-col justify-center relative overflow-y-auto max-h-screen">
        
        <h2 className="text-2xl sm:text-3xl font-bold text-[#2b6777] mb-6 text-center mt-16 sm:mt-12">
          Welcome Back 👋
        </h2>

        <form className="flex flex-col gap-5 sm:gap-6" onSubmit={handleSubmit}>
          
          {/* Email */}
          <div className="relative">
            <input
              type="email"
              id="email"
              placeholder=" "
              className="peer w-full border border-[#c8d8e4] rounded-lg px-3 sm:px-4 pt-5 pb-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#52ab98]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
            <label
              htmlFor="email"
              className="absolute left-3 top-2 text-gray-400 text-xs sm:text-sm transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-xs sm:peer-focus:text-sm peer-focus:text-[#2b6777]"
            >
              Email
            </label>
          </div>

          {/* Password */}
          <div className="relative">
            <input
              type="password"
              id="password"
              placeholder=" "
              className="peer w-full border border-[#c8d8e4] rounded-lg px-3 sm:px-4 pt-5 pb-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#52ab98]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <label
              htmlFor="password"
              className="absolute left-3 top-2 text-gray-400 text-xs sm:text-sm transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-xs sm:peer-focus:text-sm peer-focus:text-[#2b6777]"
            >
              Password
            </label>
          </div>
          
          {error && <div className="text-red-600 text-center text-xs sm:text-sm">{error}</div>}
          
          <button
            type="submit"
            className="bg-gradient-to-r from-[#2b3577] to-[#52ab98] hover:opacity-90 text-white font-semibold py-2.5 sm:py-3 rounded-xl sm:rounded-2xl shadow-lg shadow-[#2b6777]/30 transition text-sm sm:text-lg mt-2 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        {/* Forgot Password */}
        <div className="text-center text-xs sm:text-sm text-gray-500 mt-4">
          Forgot your password?{' '}
          <span
            onClick={() => setIsModalOpen(true)}
            className="text-[#2b6777] cursor-pointer font-semibold hover:underline"
          >
            Reset it
          </span>
        </div>
      </div>
    </div>
    
    {/* Password Reset Modal */}
    <PasswordResetModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    
    {/* Footer */}
    <div className="absolute bottom-3 sm:bottom-4 w-full flex items-center justify-center gap-1 sm:gap-2 text-gray-500 text-[10px] sm:text-xs px-2 text-center">
      <img src={jrslogo} alt="JRS Logo" className="h-4 sm:h-5 w-auto" />
      <span>
        © {new Date().getFullYear()}{" "}
        <a 
          href="https://jrsdynamics.com/" 
          className="text-blue-600 cursor-pointer font-semibold hover:underline"
        >
          JRS Dynamics Info Solutions
        </a>. All rights reserved.
      </span>
    </div>
  </div>
);

};

export default Login;